import { BlobProvider } from "@react-pdf/renderer";
import { CircularProgress } from "@rmwc/circular-progress";
import { Fab } from "@rmwc/fab";
import { RESSOURCE_PRE_ACHATS, RESSOURCE_PRE_ACHATS_UTILISATEUR } from "Achat/API/AchatApi";
import { URL_BASE } from "App/Constants";
import { Modale } from "App/Modale/Components/Modale";
import { PDFViewer } from "App/PDFViewer/PDFViewer";
import { showSuccess } from "App/Toast/Toast";
import moment from "moment";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { GET } from "Request/Components/RequestUtils";
import { GET_LIST_WITH_PARAMS, GET_SINGLE, useJsonRequest } from "Request/Components/useJsonRequest";
import { selectDataResourceReceived } from "Request/Store/selectors";
import { RESSOURCE_TICKET_ACHATS } from "../API/TicketClientAPI";
import { TicketClient } from "./TicketClient";
import {
  measureTicketPageHeightPt,
  TICKET_MEASURE_MAX_HEIGHT_PT,
} from "./ticketClientLayoutMeasure";

import "@rmwc/circular-progress/styles";
import "@rmwc/fab/styles";
import "./TicketClientModale.css";

const MOBILE_BREAKPOINT_PX = 768;
const MOBILE_PREVIEW_PAGE_WIDTH = 300;
const DESKTOP_PREVIEW_PAGE_WIDTH = 420;
const DESKTOP_MODAL_WIDTH = "460px";
const DESKTOP_MODAL_HEIGHT = "640px";

const getPreviewPageWidth = () => {
  if (typeof window === "undefined") {
    return DESKTOP_PREVIEW_PAGE_WIDTH;
  }

  return window.innerWidth <= MOBILE_BREAKPOINT_PX
    ? MOBILE_PREVIEW_PAGE_WIDTH
    : DESKTOP_PREVIEW_PAGE_WIDTH;
};

const getModalSize = () => {
  if (typeof window === "undefined") {
    return {
      height: DESKTOP_MODAL_HEIGHT,
      width: DESKTOP_MODAL_WIDTH,
    };
  }

  if (window.innerWidth <= MOBILE_BREAKPOINT_PX) {
    const width = Math.min(360, Math.max(280, window.innerWidth - 24));
    const height = Math.min(620, Math.max(420, window.innerHeight - 96));

    return {
      height: `${height}px`,
      width: `${width}px`,
    };
  }

  return { height: DESKTOP_MODAL_HEIGHT, width: DESKTOP_MODAL_WIDTH };
};

// #region FONCTION COMPOSANT
/**
 * Composant permettant d'afficher la modale contenant le ticket Client
 * @category Ticket Client
 */
function TicketClientModale(props) {
  //#region INITIALISATION
  const {
    achat,
    borne,
    idAchatTPE,
    onClose,
    typePaiement,
    visible,
  } = props;

  // Traduction i18n
  const { t } = useTranslation();

  // Récupération de l'historique
  const history = useHistory();

  // Initialisation des selecteurs
  const dataReceived = useSelector((state) => selectDataResourceReceived(state, RESSOURCE_TICKET_ACHATS)); // Données issues de la requête
  const dataReceivedAchat = useSelector((state) => selectDataResourceReceived(state, RESSOURCE_PRE_ACHATS)); // Données issues de la requête
  //AYB 2025.11.12 Récupération du détail des achats depuis l'historique pour le ticket client
  const dataReceivedAchatUtilisateur = useSelector((state) => selectDataResourceReceived(state, RESSOURCE_PRE_ACHATS_UTILISATEUR));

  // Initialisation du state
  const [buttonsVisible, setButtonsVisible] = useState(false);
  const [dataTicketTPE, setDataTicketTPE] = useState(null);
  const [newAchat, setNewAchat] = useState({});
  //AYB 2025.11.12 Stockage des lignes à afficher sur le ticket PDF
  const [detailAchats, setDetailAchats] = useState([]);
  // AYB 2026.04.09 Stockage de la hauteur de la page du ticket
  const [ticketPageHeightPt, setTicketPageHeightPt] = useState(null);
  // AYB 2026.04.09 Largeur d'aperçu responsive
  const [previewPageWidth, setPreviewPageWidth] = useState(getPreviewPageWidth);
  // AYB 2026.04.09 Taille responsive de la modale
  const [modalSize, setModalSize] = useState(getModalSize);
  //AYB 2025.11.13 Préservation du nom de site lorsque la ressource ne renvoie pas de libellé
  const mergeAchatData = useCallback((current, item) => {
    if (!item) {
      return current ?? {};
    }

    const normalize = (value) => {
      if (value === null || value === undefined) {
        return undefined;
      }
      const trimmed = `${value}`.trim();
      return trimmed.length ? trimmed : undefined;
    };

    const nomSiteFallback = normalize(borne?.name) ?? undefined;
    return {
      ...current,
      ...item,
      nomSite: normalize(item?.nomSite) ?? normalize(current?.nomSite) ?? nomSiteFallback,
      siteName: normalize(item?.siteName) ?? normalize(current?.siteName) ?? nomSiteFallback,
      nomTerminal: normalize(item?.nomTerminal) ?? normalize(current?.nomTerminal),
    };
  }, [borne]);
  // #endregion

  // #region REQUESTS
  // Requêtes des achats
  const requestGetAchats = useJsonRequest({
    command: GET,
    getMode: GET_LIST_WITH_PARAMS,
    resource: RESSOURCE_PRE_ACHATS,
    url: URL_BASE,
  });

  // Requête de récupération du ticket d'un achat TPE
  const requestGetTicketAchat = useJsonRequest({
    command: GET,
    getMode: GET_SINGLE,
    resource: RESSOURCE_TICKET_ACHATS,
    silent: true,
    url: URL_BASE,
  });

  const requestGetAchatUtilisateur = useJsonRequest({
    command: GET,
    getMode: GET_LIST_WITH_PARAMS,
    resource: RESSOURCE_PRE_ACHATS_UTILISATEUR,
    silent: true,
    url: URL_BASE,
  });
  // //#endregion

  // #region EVENTS
  // Fermeture de la modale
  const handleClose = () => {
    // Fermeture de la modale
    onClose();

    // AYB 2026.02.19 fromTicket pour refetch distribution (dernier service affiché)
    history.push("/", { selectedIndexTab: 1, fromTicket: true });
  };
  // #endregion

  // #endregion HOOK D'EFFET
  // Affichage des boutons
  useEffect(() => {
    if ((!achat && !dataTicketTPE) || !visible) {
      return;
    }

    setTimeout(() => {
      setButtonsVisible(true);
    }, 3500);
  }, [achat, dataTicketTPE, visible]);

  useEffect(() => {
    const onResize = () => {
      setPreviewPageWidth(getPreviewPageWidth());
      setModalSize(getModalSize());
    };

    onResize();
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  // Test s'il s'agit d'un achat via TPE
  //AYB 2025.11.12 Déclenchement du chargement du détail de commande lors de l'ouverture de la modale
  useEffect(() => {
    if (!idAchatTPE || typePaiement !== "TPE" || !visible) {
      return;
    }

    // Appel du ticket TPE
    requestGetTicketAchat(idAchatTPE);
  }, [requestGetTicketAchat, typePaiement, idAchatTPE, visible]);

  useEffect(() => {
    if (!achat) return;
    if (!achat.immatriculation) return;
    const params = [`idAchat=${achat.idAchat}`];
    setTimeout(() => {
      requestGetAchats(params);
    }, 2500);
  }, [achat, typePaiement, requestGetAchats]);

  useEffect(() => {
    if (!visible) return;
    const id = achat?.idAchat ?? idAchatTPE;
    if (!id) return;
    const params = [`page=1`, `idAchat=${id}`];
    requestGetAchatUtilisateur(params);
  }, [visible, achat, idAchatTPE, requestGetAchatUtilisateur]);

  // Réception du ticket de l'achat TPE
  useEffect(() => {
    if (!dataReceived || !visible) {
      return;
    }

    setDataTicketTPE(dataReceived.ticketClient);
  }, [dataReceived, visible]);

  // Réception du dernier achat
  useEffect(() => {
    if (!dataReceivedAchat) {
      return;
    }

    setNewAchat((current) => mergeAchatData(current, dataReceivedAchat[0]));
  }, [dataReceivedAchat, mergeAchatData]);

  useEffect(() => {
    if (!visible || !achat) return;
    if (achat.detailAchats?.length || achat.immatriculation) {
      setNewAchat((current) => mergeAchatData(current, achat));
      if (achat.detailAchats?.length) {
        setDetailAchats(achat.detailAchats);
      }
    }
  }, [visible, achat, mergeAchatData]);

  useEffect(() => {
    if (!dataReceivedAchatUtilisateur || !visible) return;

    const item = Array.isArray(dataReceivedAchatUtilisateur)
      ? dataReceivedAchatUtilisateur[0]
      : dataReceivedAchatUtilisateur;

    if (!item?.detailAchats?.length) return;

    setDetailAchats(item.detailAchats);
    setNewAchat((current) => mergeAchatData(current, item));
  }, [dataReceivedAchatUtilisateur, mergeAchatData, visible]);

  const achatPayzen = achat ?? dataTicketTPE;
  const detailList = useMemo(
    () => (detailAchats.length > 0 ? detailAchats : (achat?.detailAchats ?? [])),
    [detailAchats, achat?.detailAchats],
  );
  const ticketDataReady = (typePaiement === "TPE" && dataTicketTPE) || achat;

  useEffect(() => {
    if (!visible) {
      setTicketPageHeightPt(null);
      return undefined;
    }

    if (!ticketDataReady || !achatPayzen) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const h = await measureTicketPageHeightPt(
          <TicketClient
            achatPayzen={achatPayzen}
            achatData={newAchat}
            detailAchats={detailList}
            nomSiteFallback={borne?.name}
            pageHeightPt={TICKET_MEASURE_MAX_HEIGHT_PT}
            typePaiement={typePaiement}
          />,
        );
        if (!cancelled) {
          setTicketPageHeightPt(h);
        }
      } catch (e) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error("measureTicketPageHeightPt", e);
          setTicketPageHeightPt(595);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    visible,
    ticketDataReady,
    achatPayzen,
    newAchat,
    detailList,
    achat?.idAchat,
    borne?.name,
    typePaiement,
    dataTicketTPE,
  ]);
  // #endregion

  // #region INTERFACE
  if (!achat && !idAchatTPE) {
    return null;
  }

  // Interface
  return (
    <Modale
      height={modalSize.height}
      title={t("ticket.title")}
      visible={visible}
      width={modalSize.width}
    >
      <div className="TicketClientModale-Container">
        {ticketDataReady && ticketPageHeightPt == null ? (
          <div className="TicketClientModale-MeasureWait">
            <CircularProgress
              size="xlarge"
              style={{ color: "#FAB001" }}
            />
          </div>
        ) : null}
        {ticketDataReady && ticketPageHeightPt != null ? (
          <div className="TicketClientModale-Preview">
            <PDFViewer pageWidth={previewPageWidth}>
              <TicketClient
                achatPayzen={achatPayzen}
                achatData={newAchat}
                detailAchats={detailList}
                nomSiteFallback={borne?.name}
                pageHeightPt={ticketPageHeightPt}
                typePaiement={typePaiement}
              />
            </PDFViewer>
          </div>
        ) : null}
        {ticketDataReady && ticketPageHeightPt != null ? (
          <BlobProvider
            document={(
              <TicketClient
                achatPayzen={achatPayzen}
                achatData={newAchat}
                detailAchats={detailList}
                nomSiteFallback={borne?.name}
                pageHeightPt={ticketPageHeightPt}
                typePaiement={typePaiement}
              />
            )}
          >
            {({ url }) => {
              if (!url || !buttonsVisible) {
                return null;
              }

              return (
                <div className="TicketClientModale-ButtonContainer">
                  <a href={url} target="_blank" rel="noreferrer" className="TicketClientModale-link" download={`ticket_Client_${moment(achat ? achat.date : Date.now()).local().format("YYYYMMDD_HHmmss")}.pdf`}>
                    <Fab
                      icon="download"
                      label={t("ticket.save")}
                      onClick={() => {
                        showSuccess("Le ticket a été téléchargé");
                        setTimeout(() => {
                          handleClose();
                        }, 1500);
                      }}
                    />
                  </a>
                  <div className="TicketClientModale-ButtonClose">
                    <Fab
                      icon="close"
                      label={t("ticket.close")}
                      onClick={() => {
                        setTimeout(() => {
                          handleClose();
                        }, 1500);
                      }}
                    />
                  </div>
                </div>
              );
            }}
          </BlobProvider>
        ) : null}
      </div>
    </Modale >
  );
  // #endregion
};
// #endregion

// #region PROPRIETES
/**
 * Type des propriétés de {@link TicketClientModale}
      * @typedef {Object} TicketClientModale.propTypes
      * @property {object} [achat=null] Objet contenant l'achat effectué
      * @property {object} [borne=null] Borne/terminal courant (utilisé pour nomSite si absent de l'API)
      * @property {number} [idAchatTPE=null] Id de l'achat dans le cadre d'un paiement TPE
      * @property {function} onClose Fonction callback sur fermeture de la modale
      * @property {string} [typePaiement=null] Type de paiement
      * @property {boolean} visible True pour afficher la modale, false sinon
      */
TicketClientModale.propTypes = {
  achat: PropTypes.object,
  borne: PropTypes.object,
  idAchatTPE: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  typePaiement: PropTypes.string,
  visible: PropTypes.bool.isRequired,
};

// Valeurs par défaut des propriétés
TicketClientModale.defaultProps = {
  achat: null,
  borne: null,
  idAchatTPE: null,
  typePaiement: null
};
// #endregion

export { TicketClientModale };

